import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, X, ChevronRight, Bell, Target, BookOpen, Zap } from 'lucide-react';

export default function DailyReminderModal({ isOpen, onClose, onNavigate, userRole, missingItems }) {
  const [currentTip, setCurrentTip] = useState(0);

  const studentTips = [
    { icon: Calendar, title: "Set Your Exam Dates", message: "Add your upcoming exams to track countdown and get smart study recommendations.", action: "exam-calendar", actionText: "Set Exam Dates" },
    { icon: Zap, title: "Daily Practice", message: "Just 5 minutes of daily practice can boost your scores by 20%!", action: "daily-loop", actionText: "Start Learning" },
    { icon: Target, title: "Track Your Progress", message: "Check your exam readiness score and focus on weak areas.", action: "exam-readiness", actionText: "View Readiness" }
  ];

  const parentTips = [
    { icon: Calendar, title: "Set Exam Dates", message: "Add your child's exams to help them prepare better with targeted practice.", action: "exam-calendar", actionText: "Set Exam Dates" },
    { icon: BookOpen, title: "Create Practice Tests", message: "Generate custom tests for your child based on their weak areas.", action: "parent-test-generator", actionText: "Create Test" },
    { icon: Target, title: "Weekly Progress", message: "Review your child's weekly progress and gap analysis.", action: "weekly-report", actionText: "View Report" }
  ];

  const tips = userRole === 'parent' ? parentTips : studentTips;

  useEffect(() => {
    if (isOpen) {
      const timer = setInterval(() => {
        setCurrentTip(prev => (prev + 1) % tips.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [isOpen, tips.length]);

  if (!isOpen) return null;

  const tip = tips[currentTip];
  const Icon = tip.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-cyan-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-white" />
              <span className="text-white font-semibold">Daily Reminder</span>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <motion.div
              key={currentTip}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-violet-500/20 flex items-center justify-center mx-auto mb-4">
                <Icon className="w-8 h-8 text-violet-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{tip.title}</h3>
              <p className="text-slate-400 text-sm mb-6">{tip.message}</p>
              
              <button
                onClick={() => {
                  onNavigate(tip.action);
                  onClose();
                }}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition-all"
              >
                {tip.actionText}
                <ChevronRight className="w-5 h-5" />
              </button>
            </motion.div>

            {/* Tip indicators */}
            <div className="flex justify-center gap-2 mt-4">
              {tips.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentTip(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentTip ? 'bg-violet-500 w-6' : 'bg-slate-700'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <button
              onClick={onClose}
              className="w-full text-slate-500 hover:text-slate-300 text-sm py-2 transition-all"
            >
              Remind me later
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
